

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import SurveySelect from './select';

import Typography from '@mui/material/Typography';
import {
  useWatch,
} from 'react-hook-form';
import { reemaColors } from 'toolkit/styles/colors';
import SurveyCheckbox from './checkbox';
import SurveyDateTime from './date-time';
import SurveyInfo from './info';
import SurveyRadio from './radio';
import TextInput from './text-input';
import type { SurveyProps } from './types';

const QuestionWrapper = styled.div<{ $error: boolean }>`
  margin-bottom: 16px;
  p {
    color: ${props => (props.$error ? reemaColors.failure : 'inherit')};
  }
`;

const SurveyInput = ({
  question,
  register,
  unregister,
  setValue,
  error,
  response,
  touched,
  trigger,
  control,
  getValues,
}: SurveyProps) => {
  const [isActive, setIsActive] = useState(false);
  const inputProps = {
    register,
    unregister,
    response,
    question,
    error,
    setValue,
    control,
    isActive,
    getValues,
  };

  // register a watcher for dependent question's value
  const [watched] = useWatch({
    control,
    name: [question.dependentQuestionId],
  });

  useEffect(() => {
    // setValue used here
    if (question.dependentQuestionId) {
      if (question.dependentOptionId) {
        setIsActive(question.dependentOptionId === watched);
      } else {
        setIsActive(!!watched);
      }
    } else {
      setIsActive(true);
    }
  }, [watched]);

  useEffect(() => {
    // if a field is touched, force validations to run on it
    if (touched) {
      trigger(question.id);
    }
  }, [touched]);

  if (!isActive) return;

  return (
    <QuestionWrapper $error={!!error}>
      {question.inputType == 'info' && (
        <SurveyInfo key={question.id} {...inputProps} />
      )}
      {question.inputType != 'info' && (
        <>
          <Typography
            variant="subtitle2"
            component="p"
            sx={{ marginBottom: '4px', fontWeight: 600 }}
          >
            {question.text}
          </Typography>
          {question.inputType == 'select' && (
            <SurveySelect key={question.id} {...inputProps} />
          )}
          {question.inputType == 'radio' && (
            <SurveyRadio key={question.id} {...inputProps} />
          )}
          {question.inputType == 'checkbox' && (
            <SurveyCheckbox key={question.id} {...inputProps} />
          )}
          {['text', 'textarea', 'number'].includes(question.inputType) && (
            <TextInput key={question.id} {...inputProps} />
          )}
          {['date-time', 'date'].includes(question.inputType) && (
            <SurveyDateTime key={question.id} {...inputProps} />
          )}
        </>
      )}
    </QuestionWrapper>
  );
};

export default SurveyInput;
